import { ChatTheme } from '@/types/theme';

/**
 * Transforms database theme data to include display fields with proper defaults
 * This handles the case where the database columns might not exist yet
 */
export function transformThemeData(themeData: any): ChatTheme {
  const transformed = {
    ...themeData,
    // Set lowercase versions (from database)
    showavatar: themeData.showavatar ?? true,
    showtitle: themeData.showtitle ?? true,
    showclosebutton: themeData.showclosebutton ?? true,
    showreset: themeData.showreset ?? true,
    showsources: themeData.showsources ?? true,
    showtimestamp: themeData.showtimestamp ?? true,
    showtypingindicator: themeData.showtypingindicator ?? true,
    showuseravatar: themeData.showuseravatar ?? false,
  };
  
  // Also set camelCase versions for backward compatibility
  transformed.showAvatar = transformed.showavatar;
  transformed.showTitle = transformed.showtitle;
  transformed.showCloseButton = transformed.showclosebutton;
  transformed.showReset = transformed.showreset;
  transformed.showSources = transformed.showsources;
  transformed.showTimestamp = transformed.showtimestamp;
  transformed.showTypingIndicator = transformed.showtypingindicator;
  transformed.showUserAvatar = transformed.showuseravatar;
  
  return transformed as ChatTheme;
}

/**
 * Prepares theme data for database update by converting camelCase to lowercase
 */
export function prepareThemeForDatabase(theme: Partial<ChatTheme>): Record<string, any> {
  const dbTheme: Record<string, any> = {};
  
  // Copy all regular theme properties
  Object.keys(theme).forEach(key => {
    if (!key.startsWith('show')) {
      dbTheme[key] = theme[key as keyof ChatTheme];
    }
  });
  
  // Convert display properties to lowercase for database
  // Support both camelCase and lowercase versions
  // Priority: lowercase first (as it's the database format), then camelCase for backward compatibility
  if ('showavatar' in theme) dbTheme.showavatar = theme.showavatar;
  else if ('showAvatar' in theme) dbTheme.showavatar = theme.showAvatar;
  
  if ('showtitle' in theme) dbTheme.showtitle = theme.showtitle;
  else if ('showTitle' in theme) dbTheme.showtitle = theme.showTitle;
  
  if ('showclosebutton' in theme) dbTheme.showclosebutton = theme.showclosebutton;
  else if ('showCloseButton' in theme) dbTheme.showclosebutton = theme.showCloseButton;
  
  if ('showreset' in theme) dbTheme.showreset = theme.showreset;
  else if ('showReset' in theme) dbTheme.showreset = theme.showReset;
  
  if ('showsources' in theme) dbTheme.showsources = theme.showsources;
  else if ('showSources' in theme) dbTheme.showsources = theme.showSources;
  
  if ('showtimestamp' in theme) {
    dbTheme.showtimestamp = theme.showtimestamp;
  } else if ('showTimestamp' in theme) {
    dbTheme.showtimestamp = theme.showTimestamp;
  }
  
  if ('showtypingindicator' in theme) dbTheme.showtypingindicator = theme.showtypingindicator;
  else if ('showTypingIndicator' in theme) dbTheme.showtypingindicator = theme.showTypingIndicator;
  
  if ('showuseravatar' in theme) dbTheme.showuseravatar = theme.showuseravatar;
  else if ('showUserAvatar' in theme) dbTheme.showuseravatar = theme.showUserAvatar;
  
  return dbTheme;
}